import {
  createContext,
  Fragment,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import { Box, Flex, Icon, VisuallyHidden, type BoxProps } from '@/ui';
import { useDropdown } from './useDropdown';

type SelectOption = { id: string };

type Props<T extends SelectOption> = {
  /** Optional footer rendered below the options list (e.g. "Create custom palette" CTA). */
  footer?: ReactNode;
  /**
   * Presents the current value without offering the menu — for a setting the
   * viewer can see but isn't theirs to change.
   */
  isDisabled?: boolean;
  /** Props forwarded to the dropdown panel Box (e.g. `minW`, `right` for compact selects). */
  menuProps?: BoxProps;
  onChange: (id: T['id']) => void;
  /**
   * Called with an option's id while it is hovered, and with null once it is
   * not — for previewing the option live before it is picked. Hover is a
   * mouse-only affordance; touch users tap to apply.
   */
  onPreview?: (id: T['id'] | null) => void;
  options: readonly T[];
  /** Renders the option row content; the active checkmark and hover/active bg are handled here. */
  renderOption: (option: T, isActive: boolean) => ReactNode;
  /** Renders the trigger content (left of the chevron). */
  renderTrigger: (active: T) => ReactNode;
  /** Props forwarded to the trigger Flex. */
  triggerProps?: BoxProps;
  value: T['id'];
};

type OptionProps<T extends SelectOption> = {
  option: T;
  children: ReactNode;
  isActive: boolean;
  /**
   * Marks the option as needing a paid plan: it stays visible and clickable, but
   * carries a crown and routes the click to `onLockedClick` instead of selecting.
   * Showing it is the point — a hidden option can't be upsold. Coexists with
   * `isActive`: a locked option can be the current value (a stored default, or a
   * choice that predates a downgrade), and it still gets its checkmark.
   */
  isLocked?: boolean;
  onLockedClick?: () => void;
};

// A module-level context can't be generic: createContext runs once, while T
// only exists per SelectDropdown instantiation. The context instead carries
// the widest option shape — every T['id'] is a string — and the generic
// components narrow at the edges.
type OptionContextType = {
  /** Select an option by id and close the menu. */
  select: (id: SelectOption['id']) => void;
  /** Preview an option by id while it is hovered; null once it is not. */
  preview: (id: SelectOption['id'] | null) => void;
};

const OptionContext = createContext<OptionContextType>({
  select: () => void 0,
  preview: () => void 0,
});

export const Option = <T extends SelectOption>({
  option,
  children,
  isActive,
  isLocked,
  onLockedClick,
}: OptionProps<T>) => {
  const { preview, select } = useContext(OptionContext);

  return (
    <Box px={1.5}>
      <Flex
        _hover={isActive ? undefined : { bg: 'rgba(0,0,0,0.02)' }}
        align="center"
        aria-selected={isActive}
        as="button"
        bg={isActive ? 'magicPurple.25' : undefined}
        borderRadius="ah-md"
        cursor="pointer"
        gap={2.5}
        px={2}
        py={2}
        role="option"
        textAlign="left"
        type="button"
        w="full"
        onClick={() => (isLocked ? onLockedClick?.() : select(option.id))}
        // A locked option previews too: seeing the upgrade is the point of
        // leaving it in the list.
        onMouseEnter={() => preview(option.id)}
        onMouseLeave={() => preview(null)}
      >
        {children}
        {isLocked && (
          <>
            <Icon color="magicPurple.500" icon="Crown" size={4} />
            {/* The crown is the only cue that this option needs a paid plan, so
                name it for screen readers rather than leaving it decorative. */}
            <VisuallyHidden>Upgrade required</VisuallyHidden>
          </>
        )}
        {isActive && <Icon color="magicPurple.500" icon="Checkmark" size={4} />}
      </Flex>
    </Box>
  );
};

export const SelectDropdown = <T extends SelectOption>({
  footer,
  isDisabled,
  menuProps,
  onChange,
  onPreview,
  options,
  renderOption,
  renderTrigger,
  triggerProps,
  value,
}: Props<T>) => {
  const { close, isOpen, ref, toggle } = useDropdown();

  // Picking an option unmounts the menu without firing a mouseleave, so the
  // hovered preview has to be released on close. Held in a ref so this doesn't
  // re-run — and re-clear — every time the caller passes a new callback, and
  // synced in its own effect rather than during render, which React may
  // discard. Declared first so the close effect below always reads the
  // committed callback.
  const onPreviewRef = useRef(onPreview);
  useEffect(() => {
    onPreviewRef.current = onPreview;
  }, [onPreview]);
  useEffect(() => {
    if (!isOpen) {
      onPreviewRef.current?.(null);
    }
  }, [isOpen]);

  if (!options.length) return null;
  const active = options.find((o) => o.id === value) ?? options[0];

  return (
    <Box position="relative" ref={ref}>
      <Flex
        _hover={isDisabled ? undefined : { borderColor: 'rgba(0,0,0,0.2)' }}
        align="center"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        as="button"
        bg="white"
        borderColor="line.DEFAULT"
        borderRadius="ah-xl"
        borderStyle="solid"
        borderWidth="1px"
        cursor={isDisabled ? 'default' : 'pointer'}
        disabled={isDisabled}
        gap={2.5}
        justify="space-between"
        opacity={isDisabled ? 0.6 : 1}
        px={3}
        py={2.5}
        textAlign="left"
        transition="border-color 0.14s"
        type="button"
        w="100%"
        onClick={toggle}
        {...triggerProps}
      >
        {renderTrigger(active)}
        {/* The chevron says "there is a menu here"; there isn't one to open
            when disabled, so it goes with it. */}
        {!isDisabled && (
          <Icon
            color="ink.muted"
            flexShrink={0}
            icon={isOpen ? 'ChevronUp' : 'ChevronDown'}
            size={4}
          />
        )}
      </Flex>

      {isOpen && !isDisabled && (
        <Box
          bg="white"
          borderColor="line.DEFAULT"
          borderRadius="ah-xl"
          borderStyle="solid"
          borderWidth="1px"
          boxShadow="0 4px 12px rgba(0,0,0,0.08)"
          left={0}
          mt={1.5}
          overflow="hidden"
          pb={1}
          position="absolute"
          pt={1}
          right={0}
          role="listbox"
          top="100%"
          zIndex={10}
          {...menuProps}
        >
          <OptionContext.Provider
            value={{
              // Ids echoed back from the menu can only be ids of the rendered
              // options, so the widening to string is safely reversed here.
              select: (id) => {
                onChange(id as T['id']);
                close();
              },
              preview: (id) => onPreview?.(id as T['id'] | null),
            }}
          >
            {options.map((option) => (
              <Fragment key={option.id}>
                {renderOption(option, option.id === value)}
              </Fragment>
            ))}
          </OptionContext.Provider>
          {footer}
        </Box>
      )}
    </Box>
  );
};
